import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, TextField, Typography, List, ListItem, ListItemText,
  Checkbox, IconButton, Chip, Divider, Snackbar, Alert, Tooltip,
  LinearProgress, Collapse, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { Delete, DeleteSweep, ArrowUpward, AddTask, PlaylistAdd } from '@mui/icons-material';
import { saveToStorage, loadFromStorage } from './storageUtils';

interface Goal {
  id: number;
  text: string;
  completed: boolean;
  sessionDate: string;
}

interface GoalListProps {
  nudgeOpen?: boolean;
  onNudgeDismiss?: () => void;
}

const today = () =>
  new Date().toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });

const SUGGESTED_TASKS = [
  'Reply to booking emails',
  'Post to socials',
  'Work on funding application',
  'Follow up on invoice',
  'Update press kit',
  'Plan next rehearsal',
  'Review set list',
  'Check in with band members',
];

const GoalList: React.FC<GoalListProps> = ({ nudgeOpen = false, onNudgeDismiss }) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [newGoal, setNewGoal] = useState<string>('');
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  const [celebrationMessage, setCelebrationMessage] = useState<string>('');
  const [prevCompletedCount, setPrevCompletedCount] = useState<number>(0);
  const [newSessionDialogOpen, setNewSessionDialogOpen] = useState<boolean>(false);
  const [deletedGoal, setDeletedGoal] = useState<Goal | null>(null);
  const [showUndo, setShowUndo] = useState<boolean>(false);
  const [sessionMaxProgress, setSessionMaxProgress] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const savedGoals = loadFromStorage('goals');
    if (savedGoals && Array.isArray(savedGoals)) {
      const migrated = savedGoals.map((g: any) => ({ ...g, sessionDate: g.sessionDate || today() }));
      setGoals(migrated);
      const completedCount = migrated.filter((g: Goal) => g.completed).length;
      setPrevCompletedCount(completedCount);
      const initialPct = migrated.length > 0 ? (completedCount / migrated.length) * 100 : 0;
      setSessionMaxProgress(initialPct);
    }
  }, []);

  useEffect(() => {
    saveToStorage('goals', goals);
    const completed = goals.filter(g => g.completed).length;
    const currentPct = goals.length > 0 ? (completed / goals.length) * 100 : 0;

    // Progress bar never goes backwards within a session
    setSessionMaxProgress(prev => Math.max(prev, currentPct));

    if (completed > prevCompletedCount) {
      if (completed === goals.length && goals.length > 0) {
        setCelebrationMessage(`All ${completed} goals done. That's a great session.`);
        setShowCelebration(true);
      } else if (completed > 0 && completed % 3 === 0) {
        setCelebrationMessage(`${completed} goals completed.`);
        setShowCelebration(true);
      }
    }
    setPrevCompletedCount(completed);
  }, [goals]);

  const completedGoals = goals.filter(g => g.completed);
  const activeGoals = goals.filter(g => !g.completed);
  const progressPct = goals.length > 0 ? (completedGoals.length / goals.length) * 100 : 0;

  const handleAddGoal = (text?: string) => {
    const value = (text ?? newGoal).trim();
    if (!value) return;
    setGoals(prev => [...prev, { id: Date.now(), text: value, completed: false, sessionDate: today() }]);
    setNewGoal('');
    inputRef.current?.focus();
  };

  // Chips fill the input field — user confirms before adding, can also edit first
  const handleChipClick = (task: string) => {
    setNewGoal(task);
    inputRef.current?.focus();
  };

  const handleToggleComplete = (id: number) => {
    setGoals(prev => prev.map(g => g.id === id ? { ...g, completed: !g.completed } : g));
  };

  const handleMarkIncomplete = (id: number) => {
    setGoals(prev => prev.map(g => g.id === id ? { ...g, completed: false } : g));
  };

  const handleDeleteGoal = (id: number) => {
    const goal = goals.find(g => g.id === id);
    if (goal) {
      setDeletedGoal(goal);
      setShowUndo(true);
    }
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  const handleUndoDelete = () => {
    if (deletedGoal) {
      setGoals(prev => [...prev, deletedGoal]);
      setDeletedGoal(null);
    }
    setShowUndo(false);
  };

  const handleClearCompleted = () => {
    setGoals(prev => prev.filter(g => !g.completed));
  };

  const handleMoveToTop = (id: number) => {
    setGoals(prev => {
      const item = prev.find(g => g.id === id);
      if (!item) return prev;
      return [item, ...prev.filter(g => g.id !== id)];
    });
  };

  const handleNewSessionCarryForward = () => {
    setGoals(prev => prev.filter(g => !g.completed));
    setPrevCompletedCount(0);
    setSessionMaxProgress(0);
    setNewSessionDialogOpen(false);
  };

  const handleNewSessionFresh = () => {
    setGoals([]);
    setPrevCompletedCount(0);
    setSessionMaxProgress(0);
    setNewSessionDialogOpen(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAddGoal();
  };

  const incompletedCount = activeGoals.length;

  return (
    <Box>
      {/* Nudge from Pomodoro */}
      <Snackbar open={nudgeOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={onNudgeDismiss} autoHideDuration={7000}>
        <Alert onClose={onNudgeDismiss} severity="success" variant="filled">
          Focus session done — anything to tick off your list?
        </Alert>
      </Snackbar>

      {/* Celebration */}
      <Snackbar open={showCelebration} autoHideDuration={4000} onClose={() => setShowCelebration(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setShowCelebration(false)} sx={{ fontWeight: 600 }}>
          {celebrationMessage}
        </Alert>
      </Snackbar>

      {/* Undo delete */}
      <Snackbar open={showUndo} autoHideDuration={5000} onClose={() => setShowUndo(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setShowUndo(false)}
          action={
            <Button size="small" color="inherit" onClick={handleUndoDelete} sx={{ fontWeight: 700 }}>
              Undo
            </Button>
          }
        >
          Goal removed
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Session Goals</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${completedGoals.length} / ${goals.length}`}
            size="small"
            color={progressPct === 100 && goals.length > 0 ? 'success' : 'default'}
            sx={{ fontWeight: 700 }}
          />
          {/* Text button — unambiguous, no icon confusion */}
          <Button
            size="small"
            startIcon={<PlaylistAdd fontSize="small" />}
            onClick={() => setNewSessionDialogOpen(true)}
            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.75rem' }}
          >
            New session
          </Button>
        </Box>
      </Box>

      {/* Progress bar — never goes backwards */}
      {goals.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={sessionMaxProgress}
            color={sessionMaxProgress === 100 ? 'success' : 'primary'}
            sx={{ borderRadius: 4, height: 6 }}
          />
          {completedGoals.length > 0 && completedGoals.length < goals.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {completedGoals.length} of {goals.length} done — good progress
            </Typography>
          )}
        </Box>
      )}

      {/* Add goal input */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          inputRef={inputRef}
          placeholder="What do you want to get done this session?"
          variant="outlined"
          value={newGoal}
          onChange={e => setNewGoal(e.target.value)}
          onKeyPress={handleKeyPress}
          fullWidth
          size="small"
        />
        <Tooltip title="Add goal (or press Enter)">
          <span>
            <IconButton
              color="primary"
              onClick={() => handleAddGoal()}
              disabled={newGoal.trim() === ''}
              sx={{
                bgcolor: 'primary.main', color: '#fff', borderRadius: 2,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
              }}
            >
              <AddTask />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Suggested tasks — fills input, doesn't add instantly */}
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75, letterSpacing: 0.5 }}>
          Quick suggestions — tap to fill, then press Enter or Add
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {SUGGESTED_TASKS.map(task => (
            <Chip
              key={task}
              label={task}
              size="small"
              variant="outlined"
              clickable
              onClick={() => handleChipClick(task)}
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
        </Box>
      </Box>

      {/* Active goals */}
      {activeGoals.length > 0 && (
        <List dense disablePadding>
          {activeGoals.map(goal => (
            <ListItem
              key={goal.id}
              disablePadding
              sx={{
                mb: 0.5, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
                px: 1,
              }}
              secondaryAction={
                <Box sx={{ display: 'flex' }}>
                  <Tooltip title="Move to top of list">
                    <IconButton size="small" onClick={() => handleMoveToTop(goal.id)} sx={{ color: 'text.secondary' }}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete goal">
                    <IconButton size="small" onClick={() => handleDeleteGoal(goal.id)} sx={{ color: 'text.secondary' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <Checkbox
                edge="start"
                checked={false}
                onChange={() => handleToggleComplete(goal.id)}
                size="small"
                sx={{ mr: 0.5 }}
              />
              <ListItemText
                primary={goal.text}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Empty state */}
      {goals.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2, bgcolor: 'transparent' }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            What do you want to get done this session?
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            Type above or tap a suggestion to fill the box, then press Enter
          </Typography>
        </Paper>
      )}

      {/* Completed goals — no checkbox to prevent accidental uncheck */}
      {completedGoals.length > 0 && (
        <Collapse in>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Completed
            </Typography>
            <Button size="small" startIcon={<DeleteSweep fontSize="small" />} onClick={handleClearCompleted}
              sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.75rem' }}>
              Clear all
            </Button>
          </Box>
          <List dense disablePadding>
            {completedGoals.map(goal => (
              <ListItem
                key={goal.id}
                disablePadding
                sx={{ mb: 0.5, borderRadius: 2, opacity: 0.65, px: 1 }}
                secondaryAction={
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Mark as incomplete">
                      <Button
                        size="small"
                        onClick={() => handleMarkIncomplete(goal.id)}
                        sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
                      >
                        Undo
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete goal">
                      <IconButton size="small" onClick={() => handleDeleteGoal(goal.id)} sx={{ color: 'text.secondary' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                {/* No checkbox — completed state cannot be accidentally toggled */}
                <Box sx={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.5, flexShrink: 0 }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 900, lineHeight: 1 }}>✓</Typography>
                  </Box>
                </Box>
                <ListItemText
                  primary={goal.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { textDecoration: 'line-through', color: 'text.secondary' },
                  }}
                  secondary={goal.sessionDate}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}

      {/* New session dialog */}
      <Dialog open={newSessionDialogOpen} onClose={() => setNewSessionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Start a new session?</DialogTitle>
        <DialogContent>
          {incompletedCount > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              You have <strong>{incompletedCount} incomplete {incompletedCount === 1 ? 'goal' : 'goals'}</strong>.
              Do you want to carry {incompletedCount === 1 ? 'it' : 'them'} forward, or start completely fresh?
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              All goals are completed. Ready to start a fresh session?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
          {incompletedCount > 0 && (
            <Button onClick={handleNewSessionCarryForward} variant="contained" fullWidth>
              Keep my {incompletedCount} incomplete {incompletedCount === 1 ? 'goal' : 'goals'}
            </Button>
          )}
          <Button onClick={handleNewSessionFresh} variant={incompletedCount > 0 ? 'outlined' : 'contained'} fullWidth>
            Start fresh — clear everything
          </Button>
          <Button onClick={() => setNewSessionDialogOpen(false)} sx={{ color: 'text.secondary' }} fullWidth>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalList;
